import { useEffect, useRef } from "react";

type Props = {
  length?: number;
  onChange: (code: string) => void;
  onComplete?: (code: string) => void;
};

export default function OtpInput({ length = 4, onChange, onComplete }: Props) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const getValue = () =>
    inputs.current.map((input) => input?.value || "").join("");

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    if (value.length > 1) {
      handlePaste(value);
      return;
    }

    if (inputs.current[index]) {
      inputs.current[index]!.value = value;
    }

    if (value && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }

    const code = getValue();
    onChange(code);

    if (code.length === length) {
      onComplete?.(code);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace") {
      if (!inputs.current[index]?.value && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (paste: string) => {
    const digits = paste.replace(/\D/g, "").slice(0, length);

    digits?.split("").forEach((digit, i) => {
      if (inputs.current[i]) {
        inputs.current[i]!.value = digit;
      }
    });

    const code = digits;
    onChange(code);

    if (code.length === length) {
      onComplete?.(code);
    }
  };

  return (
    <div className="flex justify-center gap-2" dir="ltr">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            if (el) inputs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          className="h-12 w-12 rounded-xl border border-gray-300 text-center text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-50"
        />
      ))}
    </div>
  );
}
